import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { partnerApi } from '@/lib/api';
import { Building2, Plus, Search, ExternalLink } from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';

const STATUS_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  ACTIVE:    { bg: '#10b98120', text: '#34d399', border: '#10b98140' },
  TRIAL:     { bg: '#f59e0b20', text: '#fbbf24', border: '#f59e0b40' },
  SUSPENDED: { bg: '#ef444420', text: '#f87171', border: '#ef444440' },
  ARCHIVED:  { bg: '#47556920', text: '#64748b', border: '#47556940' },
};
const PLAN_STYLE: Record<string, { bg: string; text: string; border: string }> = {
  TRIAL:        { bg: '#47556920', text: '#64748b', border: '#47556940' },
  STARTER:      { bg: '#0ea5e920', text: '#38bdf8', border: '#0ea5e940' },
  PROFESSIONAL: { bg: '#6366f120', text: '#818cf8', border: '#6366f140' },
  ENTERPRISE:   { bg: '#8b5cf620', text: '#a78bfa', border: '#8b5cf640' },
};

type StatusFilter = 'ALL' | 'TRIAL' | 'ACTIVE' | 'SUSPENDED' | 'ARCHIVED';

export default function TenantsPage() {
  const navigate    = useNavigate();
  const qc          = useQueryClient();
  const [search, setSearch]             = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('ALL');
  const [showCreate, setShowCreate]     = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['partner', 'tenants', statusFilter],
    queryFn: () => partnerApi.tenants.list({ limit: 100, status: statusFilter === 'ALL' ? undefined : statusFilter }),
  });

  const filtered = (data?.data ?? []).filter((t) =>
    !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.slug.includes(search.toLowerCase()),
  );

  return (
    <div className="page">
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#e2e8f0' }}>Customer Tenants</h1>
          <p style={{ color: '#64748b', fontSize: 13, marginTop: 4 }}>
            {data?.total ?? '—'} tenants under your partner account
          </p>
        </div>
        <button
          id="new-tenant-btn"
          onClick={() => setShowCreate(true)}
          style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '9px 18px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 9, fontSize: 13, fontWeight: 600, cursor: 'pointer', boxShadow: '0 2px 12px var(--color-primary-glow)' }}
        >
          <Plus size={15} /> New tenant
        </button>
      </div>

      {/* ── Filters ── */}
      <div style={{ display: 'flex', gap: 10, marginTop: 20, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 200, maxWidth: 340 }}>
          <Search size={14} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#475569', pointerEvents: 'none' }} />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or slug…"
            style={{ width: '100%', padding: '8px 12px 8px 34px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' }}
          />
        </div>
        {(['ALL', 'TRIAL', 'ACTIVE', 'SUSPENDED', 'ARCHIVED'] as StatusFilter[]).map((s) => (
          <button key={s} onClick={() => setStatusFilter(s)} style={{
            padding: '6px 13px', borderRadius: 7, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: '1px solid',
            borderColor: statusFilter === s ? 'var(--color-primary)' : '#1e2130',
            background: statusFilter === s ? 'var(--color-primary-dim)' : 'transparent',
            color: statusFilter === s ? 'var(--color-primary)' : '#64748b',
          }}>
            {s === 'ALL' ? 'All status' : s.charAt(0) + s.slice(1).toLowerCase()}
          </button>
        ))}
      </div>

      {/* ── Loading ── */}
      {isLoading && <div style={{ padding: '40px 0', textAlign: 'center' }}><Spinner size="md" /></div>}

      {/* ── Empty ── */}
      {!isLoading && filtered.length === 0 && (
        <div style={{ padding: '56px 0', textAlign: 'center', border: '1px dashed #1e2130', borderRadius: 12, color: '#475569' }}>
          <Building2 size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
          <p style={{ margin: 0, fontSize: 14 }}>{search ? `No tenants match "${search}"` : 'No tenants yet'}</p>
          {!search && <p style={{ margin: '6px 0 0', fontSize: 12, color: '#334155' }}>Create the first tenant to get started.</p>}
        </div>
      )}

      {/* ── Table ── */}
      {!isLoading && filtered.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid #1a1f35', borderRadius: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #1a1f35' }}>
                {['Tenant', 'Plan', 'Status', 'Domain', 'Users', 'Created', ''].map((h) => (
                  <th key={h} style={{ padding: '11px 16px', textAlign: 'left', color: '#475569', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const ss = STATUS_STYLE[t.status] ?? STATUS_STYLE.ARCHIVED;
                const ps = PLAN_STYLE[t.plan]     ?? PLAN_STYLE.TRIAL;
                return (
                  <tr key={t.id} className="tableRow" style={{ borderBottom: '1px solid #12172a', cursor: 'pointer' }}
                    onClick={() => navigate({ to: '/tenants/$tenantId', params: { tenantId: t.id } })}
                  >
                    <td style={{ padding: '13px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{ width: 32, height: 32, borderRadius: 8, background: 'linear-gradient(135deg, #6366f1, #8b5cf6)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 13, color: '#fff', flexShrink: 0 }}>
                          {t.name[0].toUpperCase()}
                        </div>
                        <div>
                          <p style={{ margin: 0, fontWeight: 600, color: '#e2e8f0' }}>{t.name}</p>
                          <p style={{ margin: 0, fontSize: 11, color: '#475569', fontFamily: 'monospace' }}>@{t.slug}</p>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ps.bg, color: ps.text, border: `1px solid ${ps.border}` }}>{t.plan}</span>
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{ padding: '3px 9px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: ss.bg, color: ss.text, border: `1px solid ${ss.border}` }}>{t.status}</span>
                    </td>
                    <td style={{ padding: '13px 16px', color: '#64748b', fontSize: 12 }}>{t.allowedDomain ?? <span style={{ opacity: 0.4 }}>—</span>}</td>
                    <td style={{ padding: '13px 16px', color: '#94a3b8', fontVariantNumeric: 'tabular-nums' }}>{t.userCount ?? '—'}</td>
                    <td style={{ padding: '13px 16px', color: '#64748b', fontSize: 12, whiteSpace: 'nowrap' }}>
                      {new Date(t.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </td>
                    <td style={{ padding: '13px 16px' }}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 11px', border: '1px solid #2d3148', borderRadius: 7, color: '#94a3b8', fontSize: 12 }}>
                        <ExternalLink size={12} /> View
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && (
        <CreateTenantDrawer
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['partner', 'tenants'] }); }}
        />
      )}
      <style>{`.tableRow:hover { background: #0f1221 !important; }`}</style>
    </div>
  );
}

function CreateTenantDrawer({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [plan, setPlan] = useState<'TRIAL' | 'STARTER' | 'PROFESSIONAL' | 'ENTERPRISE'>('TRIAL');

  const derivedSlug = name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50);
  const effectiveSlug = slugTouched ? slug : derivedSlug;

  const { mutate, isPending } = useMutation({
    mutationFn: () => partnerApi.tenants.create({ name: name.trim(), slug: effectiveSlug, plan }),
    onSuccess: (t) => { toast.success(`Tenant "${t.name}" created`); onCreated(); },
  });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: '100%', maxWidth: 440, background: '#0d1022', borderLeft: '1px solid #1e2130', zIndex: 50, padding: '28px 24px', overflowY: 'auto', boxShadow: '-16px 0 60px rgba(0,0,0,0.6)', animation: 'drawerIn 0.22s cubic-bezier(0.34,1.2,0.64,1)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#e2e8f0' }}>New Tenant</h2>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#475569', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Company name <span style={{ color: '#f87171' }}>*</span></label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Acme Corporation" autoFocus style={inputStyle} />
        </div>
        <div style={{ marginBottom: 18 }}>
          <label style={labelStyle}>Slug <span style={{ color: '#f87171' }}>*</span></label>
          <input value={effectiveSlug} onChange={(e) => { setSlug(e.target.value); setSlugTouched(true); }} placeholder="acme" style={inputStyle} />
          <p style={{ margin: '5px 0 0', fontSize: 11, color: '#475569' }}>Immutable after creation. Lowercase letters, numbers, hyphens.</p>
        </div>
        <div style={{ marginBottom: 24 }}>
          <label style={labelStyle}>Plan</label>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {(['TRIAL', 'STARTER', 'PROFESSIONAL', 'ENTERPRISE'] as const).map((p) => {
              const c = PLAN_STYLE[p];
              return (
                <button key={p} onClick={() => setPlan(p)} style={{ padding: '8px 12px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', border: '1px solid', borderColor: plan === p ? c.border : '#1e2130', background: plan === p ? c.bg : 'transparent', color: plan === p ? c.text : '#64748b' }}>
                  {p.charAt(0) + p.slice(1).toLowerCase()}
                </button>
              );
            })}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Cancel</button>
          <button onClick={() => mutate()} disabled={!name.trim() || !effectiveSlug || isPending} style={{ flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!name.trim() || !effectiveSlug) ? 0.5 : 1 }}>
            {isPending ? <Spinner size="sm" /> : 'Create tenant'}
          </button>
        </div>
      </div>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

const labelStyle: React.CSSProperties = { display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 };
const inputStyle: React.CSSProperties = { width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130', borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box' };
